import React, { useState, useEffect } from 'react';
import { format } from 'date-fns';
import { parseLocalDate } from '../../utils/dateUtils';
import { useGoals } from '../../contexts/GoalContext';

const TaskCard = ({ task, onClick, onToggleComplete }) => {
    const { goals } = useGoals();
    const [goalInfo, setGoalInfo] = useState(null);

    useEffect(() => {
        if (task.goalId && goals.length > 0) {
            const goal = goals.find(g => g.id === task.goalId);
            setGoalInfo(goal);
        } else {
            setGoalInfo(null);
        }
    }, [task.goalId, goals]);

    const getTaskColor = (task) => {
        if (task.completed || task.status === 'completed') {
            return 'border-gray-300 dark:border-gray-600 bg-gray-50 dark:bg-gray-800/40 opacity-75';
        }
        const colors = {
            high: 'border-red-400 dark:border-red-500 bg-red-50 dark:bg-red-900/20',
            medium: 'border-yellow-400 dark:border-yellow-500 bg-yellow-50 dark:bg-yellow-900/20',
            low: 'border-green-400 dark:border-green-500 bg-green-50 dark:bg-green-900/20',
            default: 'border-blue-400 dark:border-blue-500 bg-blue-50 dark:bg-blue-900/20'
        };
        return colors[task.priority] || colors.default;
    };

    const getPriorityBadge = (priority) => {
        const badges = {
            high: 'bg-red-200 dark:bg-red-800 text-red-800 dark:text-red-100 border border-red-300 dark:border-red-700',
            medium: 'bg-yellow-200 dark:bg-yellow-800 text-yellow-800 dark:text-yellow-100 border border-yellow-300 dark:border-yellow-700',
            low: 'bg-green-200 dark:bg-green-800 text-green-800 dark:text-green-100 border border-green-300 dark:border-green-700',
            default: 'bg-blue-200 dark:bg-blue-800 text-blue-800 dark:text-blue-100 border border-blue-300 dark:border-blue-700'
        };
        return badges[priority] || badges.default;
    };

    const getStatusBadge = (status) => {
        const badges = {
            'not-started': 'bg-gray-200 dark:bg-gray-700 text-gray-800 dark:text-gray-200',
            'in-progress': 'bg-blue-200 dark:bg-blue-800 text-blue-800 dark:text-blue-200',
            'completed': 'bg-green-200 dark:bg-green-800 text-green-800 dark:text-green-200',
            'blocked': 'bg-red-200 dark:bg-red-800 text-red-800 dark:text-red-200'
        };
        return badges[status] || badges['not-started'];
    };

    const getStatusLabel = (status) => {
        const labels = {
            'not-started': 'Not Started',
            'in-progress': 'In Progress',
            'completed': 'Completed',
            'blocked': 'Blocked'
        };
        return labels[status] || 'Not Started';
    };

    const handleCheckboxClick = (e) => {
        e.stopPropagation();
        if (onToggleComplete) {
            onToggleComplete(task);
        }
    };

    const isCompleted = task.completed || task.status === 'completed';

    return (
        <div
            className={`w-full text-left p-4 rounded-lg border-2 transition-all hover:shadow-md dark:hover:shadow-lg ${getTaskColor(task)}`}
        >
            <div className="flex items-start space-x-3">
                {/* Completion Checkbox */}
                <button
                    onClick={handleCheckboxClick}
                    className="flex-shrink-0 mt-1 focus:outline-none focus:ring-2 focus:ring-primary-500 rounded"
                    aria-label={isCompleted ? 'Mark as incomplete' : 'Mark as complete'}
                >
                    <div className={`w-5 h-5 rounded border-2 flex items-center justify-center transition-colors ${isCompleted
                        ? 'bg-green-500 border-green-600 dark:bg-green-600 dark:border-green-700'
                        : 'bg-white dark:bg-gray-700 border-gray-300 dark:border-gray-600 hover:border-green-500 dark:hover:border-green-400'
                        }`}>
                        {isCompleted && (
                            <svg className="w-3 h-3 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                            </svg>
                        )}
                    </div>
                </button>

                {/* Task Content */}
                <button
                    onClick={() => onClick(task)}
                    className="flex-1 text-left"
                >
                    <div className="flex items-start justify-between mb-2">
                        <h3 className={`font-semibold text-gray-900 dark:text-gray-100 text-lg ${isCompleted ? 'line-through opacity-60' : ''}`}>
                            {task.task}
                        </h3>
                        <div className="flex gap-2 ml-2 flex-wrap">
                            <span className={`px-2 py-1 rounded text-xs font-medium ${getStatusBadge(task.status || 'not-started')}`}>
                                {getStatusLabel(task.status || 'not-started')}
                            </span>
                            <span className={`px-2 py-1 rounded text-xs font-medium ${getPriorityBadge(task.priority)}`}>
                                {task.priority || 'normal'}
                            </span>
                            {goalInfo && (
                                <span className={`px-2 py-1 rounded text-xs font-medium bg-indigo-100 dark:bg-indigo-900/30 text-indigo-800 dark:text-indigo-200 border border-indigo-300 dark:border-indigo-700`} title={`Goal: ${goalInfo.title}`}>
                                    <svg className="w-3 h-3 inline mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4M7.835 4.697a3.42 3.42 0 001.946-.806 3.42 3.42 0 014.438 0 3.42 3.42 0 001.946.806 3.42 3.42 0 013.138 3.138 3.42 3.42 0 00.806 1.946 3.42 3.42 0 010 4.438 3.42 3.42 0 00-.806 1.946 3.42 3.42 0 01-3.138 3.138 3.42 3.42 0 00-1.946.806 3.42 3.42 0 01-4.438 0 3.42 3.42 0 00-1.946-.806 3.42 3.42 0 01-3.138-3.138 3.42 3.42 0 00-.806-1.946 3.42 3.42 0 010-4.438 3.42 3.42 0 00.806-1.946 3.42 3.42 0 013.138-3.138z" />
                                    </svg>
                                    Goal
                                </span>
                            )}
                        </div>
                    </div>

                    <div className="space-y-1 text-sm text-gray-600 dark:text-gray-300">
                        <div className="flex items-center space-x-2">
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                            </svg>
                            <span>{typeof task.owner === 'object' ? task.owner.name : task.owner}</span>
                        </div>

                        <div className="flex items-center space-x-2">
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                            </svg>
                            <span>{format(parseLocalDate(task.dueDate), 'MMM d, yyyy')}</span>
                        </div>
                    </div>

                    {task.acceptanceCriteria && (
                        <p className="mt-2 text-sm text-gray-700 dark:text-gray-300 line-clamp-2">
                            {task.acceptanceCriteria}
                        </p>
                    )}
                </button>
            </div>
        </div>
    );
};

export default TaskCard;